import React, { memo, useCallback, useRef, useState } from "react";

import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  EditUserAvatarMutation,
  EditUserAvatarMutationVariables,
} from "gql/graphql";
import AvatarEditor from "react-avatar-editor";
import { useDropzone } from "react-dropzone";
import FileResizer from "react-image-file-resizer";
import EDIT_USER_AVATAR_MUTATION from "./networking/edit-user-avatar";
import { MinusIcon, PlusIcon } from "modules/icons";
import { useOverlay } from "modules/providers/overlay-provider";

const EditUserAvatarModal = () => {
  const { closeModal } = useOverlay();
  const avatarEditorRef = useRef<AvatarEditor>(null);
  const [url, setUrl] = useState<File>();
  const [contentType, setContentType] = useState<string>();
  const [scale, setScale] = useState<number>(0.01);

  const [editUserAvatarAPI] = useMutation<
    EditUserAvatarMutation,
    EditUserAvatarMutationVariables
  >(EDIT_USER_AVATAR_MUTATION);

  const compressImage = useCallback((file: File) => {
    FileResizer.imageFileResizer(
      file,
      512,
      512,
      "JPEG",
      90,
      0,
      (file) => {
        if (file instanceof File) {
          setUrl(file);
        }
      },
      "file",
    );
  }, []);

  const onFileDropped = useCallback(
    (fileList: Array<File>) => {
      if (fileList.length !== 1) return;
      compressImage(fileList[0]);
      setContentType(fileList[0].type);
    },
    [compressImage],
  );

  const onSaveClicked = useCallback(() => {
    const avatar = avatarEditorRef.current?.getImage()?.toDataURL();
    if (!avatar || !contentType) return;
    editUserAvatarAPI({
      variables: {
        base64Avatar: avatar,
        contentType,
      },
    });
    closeModal();
  }, [contentType, editUserAvatarAPI, closeModal]);

  const { getRootProps } = useDropzone({
    onDrop: onFileDropped,
    accept: { "image/*": [] },
    noClick: true,
    maxFiles: 1,
  });

  return (
    <Modal isOpen onClose={closeModal} size="sm" isCentered>
      <ModalOverlay />
      <ModalContent {...getRootProps()}>
        <ModalBody>
          <ModalCloseButton />
          <VStack>
            <Text fontSize={18} fontWeight={700}>
              Edit Profile Picture
            </Text>
            <Box>
              {url ? (
                <VStack>
                  <AvatarEditor
                    ref={avatarEditorRef}
                    image={url}
                    borderRadius={100}
                    scale={scale * 100}
                  />
                  <HStack w="full" spacing={4}>
                    <MinusIcon />
                    <Slider
                      defaultValue={scale}
                      max={1000}
                      min={100}
                      onChange={(value) => {
                        setScale(value / 10000);
                      }}
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                    <PlusIcon />
                  </HStack>
                  <HStack>
                    <Button
                      variant="cancel"
                      height={8}
                      w={28}
                      onClick={(e) => {
                        e.stopPropagation();
                        closeModal();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button height={8} w={28} onClick={onSaveClicked}>
                      Save
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                <Center h={300}>
                  <Text>Drop a file to start</Text>
                </Center>
              )}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(EditUserAvatarModal);
